import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FROM_PAYWALL_STATE } from '../lib/paywallNavigation';
import { getCurrentOffering, purchasePlus, restorePurchases } from '../lib/purchases';

// The in-app purchase surface required by App Review Guideline 3.1.1 (rejection
// 2026-09-18). Three of its properties are review requirements, not design choices:
//
//   1. It MUST be dismissible. A paywall with no way out is a documented rejection cause.
//   2. It MUST offer Restore Purchases, and must say so explicitly when nothing is found.
//   3. Prices MUST come from the store, never from our own constants — the user's
//      storefront decides the currency and amount, and Apple reviews the mismatch.
//
// Portalled to document.body deliberately: `.page main`'s `fadeUp` animation retains a
// transform, and a retained transform makes an element a containing block for `position:
// fixed` descendants — which centred an earlier dialog on the whole scrolled page instead
// of the viewport (see decisions/frontend-and-ux.md, 2026-09-18).
export default function Paywall({ onClose, onPurchased }) {
  const modalRef = useRef(null);
  const [offering, setOffering] = useState(undefined); // undefined = loading, null = failed
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    getCurrentOffering().then(result => { if (!cancelled) setOffering(result); });
    return () => { cancelled = true; };
  }, []);

  // The page behind an open paywall stayed scrollable, contradicting the aria-modal="true"
  // below — that tells assistive tech the background is inert while it visibly was not.
  //
  // Uses the `html` class that index.css already defines, NOT an inline body style. The
  // first attempt here set `document.body.style.overflow = 'hidden'`; it passed in jsdom
  // and changed nothing on a real iPhone, because iOS Safari/WKWebView ignores
  // overflow:hidden on <body> on its own — and native is the only place this renders.
  // `html.paywall-open` mirrors `html.chat-open`, whose comment also records that
  // `position: fixed` was tried and rejected for detaching iOS's scroll anchor.
  //
  // Adding a class rather than writing styles means this composes with any other lock
  // instead of stomping it. Scoped to the paywall deliberately: no other modal locks
  // scroll either, and making them consistent is wider than the 3.1.1 work should carry.
  useEffect(() => {
    document.documentElement.classList.add('paywall-open');
    return () => document.documentElement.classList.remove('paywall-open');
  }, []);

  useEffect(() => {
    const trigger = document.activeElement;
    modalRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); trigger?.focus?.(); };
  }, [onClose]);

  const buy = useCallback(async (pkg) => {
    setBusy(true);
    setError('');
    setNotice('');
    const res = await purchasePlus(pkg);
    setBusy(false);
    // A cancellation is a normal outcome, not a failure — showing an error for it reads
    // as "something broke" when the user simply changed their mind.
    if (res.status === 'purchased') onPurchased();
    else if (res.status === 'error') setError(res.message);
  }, [onPurchased]);

  const restore = useCallback(async () => {
    setBusy(true);
    setError('');
    setNotice('');
    const res = await restorePurchases();
    setBusy(false);
    if (res.status === 'restored') onPurchased();
    // Apple requires the "nothing found" case be stated, not silently ignored.
    else if (res.status === 'none') setNotice('No active subscription found for this account.');
    else setError(res.message);
  }, [onPurchased]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        style={{ background: 'var(--bg-surface)', borderRadius: 12, padding: '1.75rem', maxWidth: 420, width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--navy-300)' }}
      >
        {/* Pinned to the top of the scrolling sheet: the footer Close can scroll out of view
            on a short screen, and a paywall with no visible way out is a rejection cause. */}
        <div
          data-paywall-header
          style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', margin: '-1.75rem -1.75rem 0.5rem', padding: '1.25rem 1.25rem 0.25rem 1.75rem' }}
        >
          <h3 id="paywall-title" style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
            Agence Plus
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close paywall"
            style={{ width: 44, height: 44, flexShrink: 0, background: 'transparent', border: 'none', borderRadius: '50%', color: 'var(--text-secondary)', fontSize: '1.1rem', lineHeight: 1, cursor: 'pointer' }}
          >✕</button>
        </div>
        <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 1.25rem' }}>
          All 12 AI agents with 3 refreshes a day, 30 AI chat messages a day, trading
          automations with paper autopilot, and multiple linked accounts.
        </p>

        {offering === undefined && (
          <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)' }}>Loading plans…</p>
        )}

        {offering === null && (
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
            Couldn&apos;t load subscription options. Check your connection and try again.
          </p>
        )}

        {offering?.packages?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1rem' }}>
            {offering.packages.map(pkg => (
              <button
                key={pkg.identifier}
                onClick={() => buy(pkg)}
                disabled={busy}
                style={{ padding: '0.75rem 1rem', minHeight: 48, background: 'var(--navy-600)', border: 'none', borderRadius: 8, color: '#fff', cursor: busy ? 'default' : 'pointer', fontFamily: 'var(--font-body)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem' }}
              >
                {/* Fixed two-line layout — name, then terms — so every plan breaks the same
                    way instead of wrapping wherever its title length happens to fall. */}
                <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{pkg.title || pkg.identifier}</span>
                <span style={{ fontWeight: 500, fontSize: '0.85rem', opacity: 0.85 }}>
                  {pkg.priceString}
                  {pkg.period && ` / ${pkg.period}`}
                  {/* Derived from the product's own introPrice, so it cannot claim a trial
                      the user's storefront does not actually offer. */}
                  {pkg.hasFreeTrial && ' · free trial'}
                </span>
              </button>
            ))}
          </div>
        )}

        {error && (
          <p role="alert" className="error" style={{ marginBottom: '0.75rem' }}>{error}</p>
        )}
        {notice && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>{notice}</p>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{ flex: '1 1 120px', padding: '0.6rem', minHeight: 44, background: 'transparent', border: '1px solid var(--navy-300)', borderRadius: 8, color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600 }}
          >Close</button>
          <button
            onClick={restore}
            disabled={busy}
            style={{ flex: '1 1 120px', padding: '0.6rem', minHeight: 44, background: 'transparent', border: '1px solid var(--navy-300)', borderRadius: 8, color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600 }}
          >Restore purchases</button>
        </div>

        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '1rem 0 0', lineHeight: 1.5 }}>
          Billed through your app store. Subscriptions renew automatically until cancelled
          in your app store account settings.
        </p>

        {/* Guideline 3.1.2(c) (rejection 2026-09-30): the purchase flow must link the Terms
            of Use (EULA) and privacy policy. In-app routes, so they work offline and never
            leave the app; closing first so the page is not hidden behind this modal. */}
        <p style={{ fontSize: '0.75rem', margin: '0.5rem 0 0', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to="/terms" state={FROM_PAYWALL_STATE} onClick={onClose} style={{ color: 'var(--text-secondary)' }}>Terms of Use</Link>
          <Link to="/privacy" state={FROM_PAYWALL_STATE} onClick={onClose} style={{ color: 'var(--text-secondary)' }}>Privacy Policy</Link>
        </p>
      </div>
    </div>,
    document.body
  );
}
